import { Controller } from '@hotwired/stimulus'
import { trackSharedContent } from '../../../javascript/shared_content_tracking'

export default class extends Controller {
  static targets = ['menu', 'trigger', 'copyLabel', 'audio']
  static values = {
    url: String,
    sharesUrl: String,
    shareableId: String,
    shareableType: String
  }

  connect () {
    this.watchForAudioPlayer()
  }

  disconnect () {
    if (this.audioObserver) this.audioObserver.disconnect()
  }

  // Trinity Audio is injected client-side (via GTM), so the player may not exist
  // yet — or at all. Reveal the Audio button only once a player shows up.
  watchForAudioPlayer () {
    if (!this.hasAudioTarget) return

    if (this.trinityPlayer()) {
      this.revealAudio()
      return
    }

    this.audioObserver = new MutationObserver(() => {
      if (this.trinityPlayer()) this.revealAudio()
    })
    this.audioObserver.observe(document.body, { childList: true, subtree: true })
  }

  trinityPlayer () {
    return document.querySelector('.trinity-player')
  }

  revealAudio () {
    this.audioTarget.classList.remove('hidden')
    this.audioTarget.classList.add('inline-flex')
    if (this.audioObserver) this.audioObserver.disconnect()
  }

  scrollToAudio () {
    const player = this.trinityPlayer()
    if (player) player.scrollIntoView({ behavior: 'smooth', block: 'start' })
    this.captureClick('audio')
  }

  print () {
    this.captureClick('print')
    window.print()
  }

  toggle (event) {
    event.stopPropagation()
    this.menuTarget.classList.contains('hidden') ? this.open() : this.close()
  }

  open () {
    this.menuTarget.classList.remove('hidden')
    this.triggerTarget.setAttribute('aria-expanded', 'true')
    this.captureClick('open_menu')
  }

  close () {
    this.menuTarget.classList.add('hidden')
    this.triggerTarget.setAttribute('aria-expanded', 'false')
  }

  closeOnOutside (event) {
    if (!this.element.contains(event.target)) this.close()
  }

  closeOnEscape (event) {
    if (event.key === 'Escape') this.close()
  }

  copyLink (event) {
    event.preventDefault()

    const url = this.urlValue || window.location.href
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url)
    }

    if (this.hasCopyLabelTarget) {
      const original = this.copyLabelTarget.textContent
      this.copyLabelTarget.textContent = 'Link Copied'
      setTimeout(() => { this.copyLabelTarget.textContent = original }, 1500)
    }

    this.capture('copy_link')
  }

  track (event) {
    this.capture(event.currentTarget.dataset.shareMethod)
  }

  recordEmail () {
    if (this.hasSharesUrlValue && this.hasShareableIdValue) {
      const headers = new Headers()
      headers.append('Content-Type', 'application/json')
      headers.append('Accept', 'application/html')
      headers.append('X-CSRF-Token', Rails.csrfToken())

      fetch(this.sharesUrlValue, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          id: this.shareableIdValue,
          type: this.shareableTypeValue
        })
      })
    }

    this.capture('email')
  }

  capture (method) {
    trackSharedContent(method)
  }

  // Shelf clicks that aren't a share (opening the menu, print, audio), so
  // they don't inflate shared_content.
  captureClick (action) {
    if (typeof window.posthog === 'undefined') return
    window.posthog.capture('share_shelf_clicked', {
      action,
      source: window.location.pathname
    })
  }
}
